import { ProcessingFeeBasis } from "@redotech/cockroachdb-codegen/tables/fulfillment/processing-fee-basis";
import * as z from "zod";

export interface ReturnsCenterPricing {
  readonly fee: number | null;
  readonly basis: ProcessingFeeBasis | null;
}

export interface ProcessingBillingConfig {
  readonly global: {
    readonly fee: number | null;
    readonly basis: ProcessingFeeBasis;
  };
  readonly centers: Readonly<Record<string, ReturnsCenterPricing>>;
}

export interface ProcessingBillingChange {
  readonly path: string;
  readonly prev: unknown;
  readonly next: unknown;
}

export const returnsCenterPricingSchema = z
  .object({
    fee: z.number().nullable(),
    basis: z.enum(ProcessingFeeBasis).nullable(),
  })
  .refine((pricing) => pricing.fee == null || pricing.basis != null, {
    message: "A returns-center fee override requires a fee basis",
  });

export const processingBillingConfigSchema = z.object({
  global: z.object({
    fee: z.number().nullable(),
    basis: z.enum(ProcessingFeeBasis),
  }),
  centers: z.record(z.string(), returnsCenterPricingSchema),
});

export const PROCESSING_COST_PATH = "redoManagedReturns.processingCost";

export const PROCESSING_FEE_PATH = "redoManagedReturns.processingFee";

export const PROCESSING_FEE_BASIS_PATH =
  "redoManagedReturns.processingFeeBasis";

export const RETURNS_CENTER_PRICING_PATH =
  "redoManagedReturns.returnsCenterPricing";

export const DEFAULT_PROCESSING_BILLING_CONFIG: ProcessingBillingConfig = {
  global: { fee: null, basis: ProcessingFeeBasis.PerReturn },
  centers: {},
};

export function returnsCenterPricingPath(returnsCenterId: string): string {
  return `${RETURNS_CENTER_PRICING_PATH}.${returnsCenterId}`;
}

export function parseReturnsCenterPricingPath(path: string): string | null {
  const prefix = `${RETURNS_CENTER_PRICING_PATH}.`;
  return path.startsWith(prefix) ? path.slice(prefix.length) : null;
}

export function isProcessingFeeBasisPath(path: string): boolean {
  return path === PROCESSING_FEE_BASIS_PATH;
}

export function isProcessingBillingPath(path: string): boolean {
  return (
    path === PROCESSING_COST_PATH ||
    path === PROCESSING_FEE_PATH ||
    path === PROCESSING_FEE_BASIS_PATH ||
    parseReturnsCenterPricingPath(path) !== null
  );
}

export function parseProcessingFeeBasisValue(
  value: unknown,
): ProcessingFeeBasis | null {
  return value == null ? null : z.enum(ProcessingFeeBasis).parse(value);
}

export function parseProcessingFeeValue(value: unknown): number | null {
  return value == null ? null : z.number().parse(value);
}

export function parseReturnsCenterPricingValue(
  value: unknown,
): ReturnsCenterPricing | null {
  return value == null ? null : returnsCenterPricingSchema.parse(value);
}

export interface StoredProcessingBillingRow {
  readonly returnsCenterId: string | null;
  readonly fee: number | null;
  readonly basis: ProcessingFeeBasis | null;
}

export function readProcessingBillingConfig(
  rows: readonly StoredProcessingBillingRow[],
): ProcessingBillingConfig {
  const global = rows.find((row) => row.returnsCenterId === null);
  const centers: Record<string, ReturnsCenterPricing> = {};
  for (const row of rows) {
    if (row.returnsCenterId != null && (row.fee != null || row.basis != null)) {
      centers[row.returnsCenterId] = { fee: row.fee, basis: row.basis };
    }
  }
  return {
    global: {
      fee: global?.fee ?? null,
      basis: global?.basis ?? DEFAULT_PROCESSING_BILLING_CONFIG.global.basis,
    },
    centers,
  };
}

function centerPricingOrNull(
  pricing: ReturnsCenterPricing | undefined,
): ReturnsCenterPricing | null {
  if (!pricing || (pricing.fee == null && pricing.basis == null)) {
    return null;
  }
  return pricing;
}

function samePricing(
  a: ReturnsCenterPricing | null,
  b: ReturnsCenterPricing | null,
): boolean {
  return a?.fee === b?.fee && a?.basis === b?.basis;
}

export function diffProcessingBillingConfig(
  prev: ProcessingBillingConfig,
  next: ProcessingBillingConfig,
): ProcessingBillingChange[] {
  const changes: ProcessingBillingChange[] = [];
  if (prev.global.fee !== next.global.fee) {
    changes.push({
      path: PROCESSING_COST_PATH,
      prev: prev.global.fee,
      next: next.global.fee,
    });
  }
  if (prev.global.basis !== next.global.basis) {
    changes.push({
      path: PROCESSING_FEE_BASIS_PATH,
      prev: prev.global.basis,
      next: next.global.basis,
    });
  }
  const centerIds = new Set([
    ...Object.keys(prev.centers),
    ...Object.keys(next.centers),
  ]);
  for (const returnsCenterId of [...centerIds].sort()) {
    const before = centerPricingOrNull(prev.centers[returnsCenterId]);
    const after = centerPricingOrNull(next.centers[returnsCenterId]);
    if (!samePricing(before, after)) {
      changes.push({
        path: returnsCenterPricingPath(returnsCenterId),
        prev: before,
        next: after,
      });
    }
  }
  return changes;
}
